import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { UserRound, LogOut, Inbox, ShoppingCart, CalendarDays, Download, Loader2 } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { api, formatApiError } from "@/lib/api";

const STATUS_COLORS = { nouveau: "var(--orange)", "en cours": "var(--ink)", traité: "#1E8A3C" };

function AuthForms() {
  const { login, register } = useAuth();
  const { t } = useLang();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else if (mode === "register") await register(form.name, form.email, form.password);
      else {
        const { data } = await api.post("/auth/forgot-password", { email: form.email });
        toast.success(data.message);
        setMode("login");
        setLoading(false);
        return;
      }
      toast.success(t("Bienvenue dans votre espace client !", "Welcome to your client area!"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto" data-testid="client-auth-forms">
      <div className="flex" style={{ border: "1px solid var(--border)" }}>
        {[["login", t("Connexion", "Sign in")], ["register", t("Créer un compte", "Create account")]].map(([k, l]) => (
          <button key={k} onClick={() => setMode(k)} className="flex-1 py-3 text-sm font-bold transition-colors" data-testid={`auth-tab-${k}`}
            style={{ background: mode === k ? "var(--ink)" : "var(--white)", color: mode === k ? "var(--bone)" : "var(--ink)" }}>{l}</button>
        ))}
      </div>
      <form onSubmit={submit} className="mt-6 space-y-4 p-8" style={{ background: "var(--white)", border: "1px solid var(--border)" }} data-testid="client-auth-form">
        {mode === "register" && (
          <input className="field" required minLength={2} placeholder={t("Nom complet *", "Full name *")} value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="client-name-input" />
        )}
        <input className="field" type="email" required placeholder="Email *" value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="client-email-input" />
        {mode !== "forgot" && (
          <input className="field" type="password" required minLength={mode === "register" ? 8 : 1}
            placeholder={t(mode === "register" ? "Mot de passe (8 caractères min.) *" : "Mot de passe *", mode === "register" ? "Password (min. 8 characters) *" : "Password *")}
            value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="client-password-input" />
        )}
        <button type="submit" disabled={loading} className="btn-primary w-full py-4 font-semibold flex items-center justify-center gap-2" data-testid="client-auth-submit">
          {loading ? <Loader2 size={17} className="animate-spin" /> : mode === "login" ? t("Me connecter", "Sign in") : mode === "register" ? t("Créer mon compte", "Create my account") : t("Recevoir le lien de réinitialisation", "Send reset link")}
        </button>
        {mode === "login" && (
          <button type="button" onClick={() => setMode("forgot")} className="w-full text-center text-sm underline underline-offset-4" style={{ color: "var(--ink-2)" }} data-testid="forgot-password-link">
            {t("Mot de passe oublié ?", "Forgot password?")}
          </button>
        )}
        {mode === "forgot" && (
          <button type="button" onClick={() => setMode("login")} className="w-full text-center text-sm underline underline-offset-4" style={{ color: "var(--ink-2)" }} data-testid="back-to-login-link">
            {t("← Retour à la connexion", "← Back to sign in")}
          </button>
        )}
        <p className="text-xs text-center" style={{ color: "var(--ink-2)" }}>
          {t("Vos devis, commandes et inscriptions sont liés à votre adresse email.", "Your quotes, orders and registrations are linked to your email address.")}
        </p>
      </form>
    </div>
  );
}

function Dashboard() {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const [tab, setTab] = useState("quotes");
  const [quotes, setQuotes] = useState([]);
  const [orders, setOrders] = useState([]);
  const [regs, setRegs] = useState([]);

  const load = useCallback(() => {
    api.get("/me/quotes").then((r) => setQuotes(r.data)).catch(() => {});
    api.get("/me/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/me/session-registrations").then((r) => setRegs(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const fmtDate = (iso) => new Date(iso).toLocaleDateString("fr-FR");
  const TABS = [
    { key: "quotes", label: t("Mes devis", "My quotes"), icon: Inbox, count: quotes.length },
    { key: "orders", label: t("Mes commandes", "My orders"), icon: ShoppingCart, count: orders.length },
    { key: "sessions", label: t("Mes formations", "My training"), icon: CalendarDays, count: regs.length },
  ];

  return (
    <div data-testid="client-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-display font-black text-3xl">{t("Bonjour", "Hello")} {user.name} 👋</h2>
          <p className="text-sm mt-1" style={{ color: "var(--ink-2)" }}>{user.email}</p>
        </div>
        <button onClick={logout} className="btn-outline px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2" data-testid="client-logout-btn">
          <LogOut size={15} /> {t("Déconnexion", "Sign out")}
        </button>
      </div>

      <div className="flex mt-8" style={{ border: "1px solid var(--border)" }}>
        {TABS.map((tb) => (
          <button key={tb.key} onClick={() => setTab(tb.key)} className="flex-1 py-3.5 text-sm font-bold flex items-center justify-center gap-2 transition-colors" data-testid={`client-tab-${tb.key}`}
            style={{ background: tab === tb.key ? "var(--ink)" : "var(--white)", color: tab === tb.key ? "var(--bone)" : "var(--ink)" }}>
            <tb.icon size={15} /> {tb.label} ({tb.count})
          </button>
        ))}
      </div>

      <div className="mt-6 space-y-3">
        {tab === "quotes" && (quotes.length ? quotes.map((q) => (
          <div key={q.id} className="p-6 flex flex-wrap items-center justify-between gap-4" style={{ background: "var(--white)", border: "1px solid var(--border)" }} data-testid={`client-quote-${q.id}`}>
            <div>
              <p className="font-bold">{q.subject}</p>
              <p className="text-xs mt-1" style={{ color: "var(--ink-2)" }}>Réf. {q.id.slice(0, 8)} · {fmtDate(q.created_at)}</p>
              {q.proposal_sent_at && (
                <p className="text-xs mt-1 font-semibold" style={{ color: "#1E8A3C" }}>{t("Devis envoyé le", "Quote sent on")} {fmtDate(q.proposal_sent_at)}{q.followup_sent ? t(" · relance envoyée", " · reminder sent") : ""}</p>
              )}
            </div>
            <div className="flex items-center gap-3">
              {q.proposal_sent_at && (
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/quotes/${q.id}/proposal`} target="_blank" rel="noopener noreferrer"
                  className="btn-outline px-4 py-2 text-xs font-bold inline-flex items-center gap-2" data-testid={`client-proposal-${q.id}`}>
                  <Download size={13} /> {t("Devis PDF", "Quote PDF")}
                </a>
              )}
              <span className="text-xs font-bold uppercase tracking-wider px-3 py-1.5" style={{ background: q.status === "accepté" ? "#1E8A3C" : STATUS_COLORS[q.status] || "var(--ink)", color: "var(--bone)" }}>{q.status}</span>
            </div>
          </div>
        )) : <Empty text={t("Aucun devis pour le moment.", "No quotes yet.")} cta={t("Demander un devis", "Request a quote")} to="/contact" />)}

        {tab === "orders" && (orders.length ? orders.map((o) => (
          <div key={o.session_id} className="p-6 flex flex-wrap items-center justify-between gap-4" style={{ background: "var(--white)", border: "1px solid var(--border)" }} data-testid={`client-order-${o.session_id}`}>
            <div>
              <p className="font-bold">{(o.amount || 0).toFixed(2)} € — {t("Commande payée", "Paid order")}</p>
              <p className="text-xs mt-1" style={{ color: "var(--ink-2)" }}>{fmtDate(o.created_at)}</p>
            </div>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/orders/${o.session_id}/invoice`} target="_blank" rel="noopener noreferrer"
              className="btn-outline px-4 py-2 text-xs font-bold inline-flex items-center gap-2" data-testid={`client-invoice-${o.session_id}`}>
              <Download size={13} /> {t("Facture PDF", "PDF invoice")}
            </a>
          </div>
        )) : <Empty text={t("Aucune commande pour le moment.", "No orders yet.")} cta={t("Voir le catalogue", "Browse the catalogue")} to="/catalogue" />)}

        {tab === "sessions" && (regs.length ? regs.map((r) => {
          const past = r.session_date <= new Date().toISOString().slice(0, 10);
          return (
            <div key={r.id} className="p-6 flex flex-wrap items-center justify-between gap-4" style={{ background: "var(--white)", border: "1px solid var(--border)" }} data-testid={`client-reg-${r.id}`}>
              <div>
                <p className="font-bold">{r.formation_title}</p>
                <p className="text-xs mt-1" style={{ color: "var(--ink-2)" }}>
                  {new Date(r.session_date + "T00:00:00").toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })} · {r.city} · {r.participants} participant(s)
                </p>
              </div>
              <div className="flex items-center gap-3">
                {past ? (
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/me/attestations/${r.id}`} target="_blank" rel="noopener noreferrer"
                    className="btn-outline px-4 py-2 text-xs font-bold inline-flex items-center gap-2" data-testid={`client-attestation-${r.id}`}>
                    <Download size={13} /> {t("Attestation PDF", "Certificate PDF")}
                  </a>
                ) : (
                  <span className="text-xs font-bold uppercase tracking-wider px-3 py-1.5" style={{ background: "#1E8A3C", color: "var(--bone)" }}>{t("Confirmée", "Confirmed")}</span>
                )}
              </div>
            </div>
          );
        }) : <Empty text={t("Aucune inscription à une session.", "No session registrations.")} cta={t("Voir les sessions", "See sessions")} to="/formations" />)}
      </div>
    </div>
  );
}

function Empty({ text, cta, to }) {
  return (
    <div className="p-10 text-center" style={{ background: "var(--white)", border: "1px dashed var(--border)" }}>
      <p style={{ color: "var(--ink-2)" }}>{text}</p>
      <Link to={to} className="btn-primary inline-block mt-4 px-6 py-3 text-sm font-semibold">{cta}</Link>
    </div>
  );
}

export default function EspaceClient() {
  const { user, checked } = useAuth();
  const { t } = useLang();
  useEffect(() => { window.scrollTo(0, 0); document.title = t("Espace client — ALDOL-SOLUTIONS", "Client area — ALDOL-SOLUTIONS"); }, [t]);

  return (
    <div className="pt-[148px] min-h-screen" data-testid="espace-client-page">
      <section className="max-w-[1000px] mx-auto px-5 md:px-10 py-16 md:py-24">
        <Reveal>
          <p className="overline mb-4 flex items-center gap-2" style={{ color: "var(--orange)" }}><UserRound size={15} /> {t("Espace client", "Client area")}</p>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none mb-10">
            {user && checked ? t("Votre espace personnel.", "Your personal area.") : t("Retrouvez tous vos documents.", "Find all your documents.")}
          </h1>
        </Reveal>
        {!checked ? null : user ? <Dashboard /> : <AuthForms />}
      </section>
    </div>
  );
}
